/* import __COLOCATED_TEMPLATE__ from './classification-attribute-selector.hbs'; */
/* RESPONSIBLE TEAM: team-fin-voice */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type RouterService from '@ember/routing/router-service';
import type ConversationAttributeDescriptor from 'embercom/objects/inbox/conversation-attribute-descriptor';

interface Args {
  selectedAttributes?: string[];
  onAttributeChange: (attribute: string, updatedList: string[]) => void;
  onAttributeRemove: (attribute: string, updatedList: string[]) => void;
  onNavigateToSettings: () => void;
  allClassifiableListAttributes: ConversationAttributeDescriptor[];
  isSaving: boolean;
  disabledMessage?: string;
}

interface Signature {
  Element: HTMLElement;
  Args: Args;
}

type Attribute = {
  id: string;
  attribute: ConversationAttributeDescriptor;
  isDirectlySelected: boolean;
  parentAttribute?: ConversationAttributeDescriptor;
};

export default class ClassificationAttributeSelector extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare router: RouterService;

  @tracked selectedAttributes = new Set<string>(this.args.selectedAttributes || []);
  @tracked allActiveAttributes: Attribute[] = [];

  get filteredAllClassifiableListAttributes() {
    return this.args.allClassifiableListAttributes
      .filter((attribute) => {
        return !this.allActiveAttributes.some((attr) => attr.attribute.id === attribute.id);
      })
      .map((attribute) => {
        return {
          value: String(attribute.id),
          text: attribute.name,
        };
      });
  }

  get savingDisabled() {
    return this.args.isSaving;
  }

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);

    this._populateAllActiveAttributes();
  }

  /**
   * Recursively removes all child attributes from selectedAttributes when a parent attribute is selected.
   * A parent attribute has dependentConditions that point to child attributes.
   * This function recursively removes all descendants of the parent.
   */
  private _removeChildAttributesRecursively(parentAttributeId: string | number): void {
    let parentAttribute = this.args.allClassifiableListAttributes.find(
      (attr) => attr.id === parentAttributeId,
    );

    if (!parentAttribute || !parentAttribute.dependentConditions.length) {
      return;
    }

    // Get all child attribute IDs from dependent conditions
    let childAttributeIds = parentAttribute.dependentConditions.map(
      (condition) => condition.descriptorId,
    );

    // Remove each child attribute from selectedAttributes
    childAttributeIds.forEach((childId) => {
      let childIdString = String(childId);
      if (this.selectedAttributes.has(childIdString)) {
        this.selectedAttributes.delete(childIdString);

        // Recursively remove children of this child
        this._removeChildAttributesRecursively(childId);
      }
    });
  }

  /**
   * Populates allActiveAttributes with directly selected attributes and their children recursively.
   * Directly selected attributes have isDirectlySelected = true, while their children have isDirectlySelected = false.
   */
  private _populateAllActiveAttributes(): void {
    this.allActiveAttributes = [];

    // Process each selected attribute
    this.selectedAttributes.forEach((attributeIdString) => {
      let attributeId = String(attributeIdString);
      let attribute = this.args.allClassifiableListAttributes.find(
        (attr) => String(attr.id) === attributeId,
      );

      if (!attribute) {
        return;
      }

      // Add the directly selected attribute
      this.allActiveAttributes.push({
        id: attributeIdString,
        attribute,
        isDirectlySelected: true,
      });

      // Recursively add all children with isDirectlySelected = false
      this._addChildrenToActiveAttributes(attribute, false);
    });
  }

  /**
   * Recursively adds all children of an attribute to allActiveAttributes.
   * @param parentAttribute - The parent attribute whose children should be added
   * @param isDirectlySelected - Whether the children are directly selected (false for nested children)
   */
  private _addChildrenToActiveAttributes(
    parentAttribute: ConversationAttributeDescriptor,
    isDirectlySelected: boolean,
  ): void {
    if (!parentAttribute.dependentConditions.length) {
      return;
    }

    // Get all child attribute IDs from dependent conditions
    let childAttributeIds = parentAttribute.dependentConditions.map(
      (condition) => condition.descriptorId,
    );

    // Add each child attribute to allActiveAttributes
    childAttributeIds.forEach((childId) => {
      let childIdString = String(childId);
      let childAttribute = this.args.allClassifiableListAttributes.find(
        (attr) => attr.id === childId,
      );

      if (!childAttribute) {
        return;
      }

      // Only add if not already present (to avoid duplicates)
      if (!this.allActiveAttributes.find((attr) => attr.id === childIdString)) {
        this.allActiveAttributes.push({
          id: childIdString,
          attribute: childAttribute,
          isDirectlySelected,
          parentAttribute,
        });

        // Recursively add children of this child with isDirectlySelected = false
        this._addChildrenToActiveAttributes(childAttribute, false);
      }
    });
  }

  @action
  addSelectedAttribute(attribute: string) {
    let attributeObject = this.args.allClassifiableListAttributes.find(
      (attr) => String(attr.id) === attribute,
    );

    if (!attributeObject) {
      return;
    }

    this.selectedAttributes.add(attribute);

    // Remove all child attributes recursively when parent is selected
    this._removeChildAttributesRecursively(attributeObject.id);

    // Update allActiveAttributes to reflect the new selection
    this._populateAllActiveAttributes();

    if (this.args.onAttributeChange) {
      this.args.onAttributeChange(attribute, Array.from(this.selectedAttributes));
    }
  }

  @action
  removeSelectedAttribute(attribute: string) {
    this.selectedAttributes.delete(attribute);

    this._populateAllActiveAttributes();

    if (this.args.onAttributeRemove) {
      this.args.onAttributeRemove(attribute, Array.from(this.selectedAttributes));
    }
  }

  @action
  getTooltipText(parentAttribute?: ConversationAttributeDescriptor) {
    if (!parentAttribute) {
      return '';
    }

    return this.intl.t(
      'operator.fin.setup.tabs.setup-and-go-live.phone.fin-issue-categorization.category-tooltip',
      {
        parentName: parentAttribute.name,
      },
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::Components::ClassificationAttributeSelector': typeof ClassificationAttributeSelector;
  }
}
